import { ApiService } from './index'
import { Inspection, ApiResponse } from '@types'

/**
 * Real backend contract (apps.inspections.serializers.InspectionCreateSerializer /
 * InspectionSerializer, mounted at /api/v1/inspections/) uses field names
 * hive/template/inspection_date/inspection_data/weather_* rather than the frontend's
 * `Inspection` shape (hive_id/checklist_id/date/items/...). Translate at this boundary so
 * every caller of inspectionsApi can keep using the frontend shape.
 */
const toBackendPayload = (data: Partial<Inspection>): Record<string, any> => {
  const payload: Record<string, any> = {}
  if (data.hive_id !== undefined) payload.hive = data.hive_id
  if (data.checklist_id !== undefined) payload.template = data.checklist_id
  if (data.date !== undefined) payload.inspection_date = data.date
  if (data.notes !== undefined) payload.notes = data.notes
  if (data.attention !== undefined) payload.attention = data.attention
  if (data.reminder !== undefined) payload.reminder = data.reminder
  if (data.reminder_date !== undefined) payload.reminder_date = data.reminder_date
  if (data.impression !== undefined) payload.impression = data.impression
  if (data.weather !== undefined) payload.weather_conditions = data.weather
  if (data.temperature !== undefined) payload.weather_temperature = data.temperature
  return payload
}

export const inspectionsApi = {
  // Inspections
  getInspections: (hiveId?: number, params?: {
    limit?: number
    offset?: number
    search?: string
    start_date?: string
    end_date?: string
  }) => {
    const url = hiveId ? `inspections/hive/${hiveId}/` : 'inspections/'
    return ApiService.get<ApiResponse<Inspection[] | { hive_id: number; hive_name: string; inspections: Inspection[] }>>(url, { params })
  },

  getInspection: (id: number) =>
    ApiService.get<ApiResponse<Inspection>>(`inspections/${id}/`),

  createInspection: (data: Partial<Inspection>) =>
    ApiService.post<ApiResponse<Inspection>>('inspections/', toBackendPayload(data)),

  updateInspection: (id: number, data: Partial<Inspection>) =>
    ApiService.patch<ApiResponse<Inspection>>(`inspections/${id}/`, toBackendPayload(data)),

  deleteInspection: (id: number) =>
    ApiService.delete(`inspections/${id}/`),

  // Inspection images (read-only listing; uploads happen via the main create call's
  // multipart `images` field, see InspectionCreate.tsx)
  getInspectionImages: (inspectionId: number) =>
    ApiService.get<ApiResponse<any[]>>(`inspections/${inspectionId}/images/`),

  // Inspection export
  exportInspections: (params?: {
    hive_id?: number
    start_date?: string
    end_date?: string
    format?: 'csv' | 'xlsx' | 'pdf'
  }) =>
    ApiService.get('inspections/export/', {
      params,
      responseType: 'blob'
    }),
}
